import DOMPurify from 'dompurify';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';

import Button from 'components/Button/Button';
import Toast from 'components/Toast/Toast';
import useClipboard from 'hooks/useClipboard';
import { useOpportunitiesStore } from 'store';
import { decodeHtml } from 'utils';

import { JoinUsPageType } from '../../JoinUsPage.types';

import * as S from './JoinUsOpportunityView.styles';

export interface JoinUsOpportunityViewProps {
  pageData: JoinUsPageType;
}

const defaultProps: Partial<JoinUsOpportunityViewProps> = {};

const JoinUsOpportunityView: React.FunctionComponent<
  JoinUsOpportunityViewProps
> = ({ pageData, ...rest }) => {
  const { activeOpportunity } = useOpportunitiesStore();
  const { isCopied, copyToClipboard } = useClipboard();
  const router = useRouter();

  const handleShare = (type: string) => {
    if (typeof window === 'undefined') return;

    const shareUrl = `${window.location.origin}${router.asPath}`;
    const shareTitle = activeOpportunity?.title || '';

    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);

    const shareMap: Record<string, string> = {
      Facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      X: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      LinkedIn: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    };

    if (shareMap[type]) {
      window.open(shareMap[type], '_blank', 'noopener,noreferrer');
    }

    if (type === 'Copy link') {
      copyToClipboard(shareUrl);
    }
  };

  const safeHtml = useMemo(() => {
    const rawContent = activeOpportunity?.content || '';

    if (typeof window === 'undefined') {
      return rawContent;
    }

    return DOMPurify.sanitize(decodeHtml(rawContent));
  }, [activeOpportunity?.content]);

  return (
    <S.JoinUsOpportunityViewWrapper
      {...rest}
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {isCopied && <Toast text="✓" variant="dark" />}

      <S.HeaderWrapper>
        <S.Header>
          <S.Label>{pageData.jobOverlayJobTitle}</S.Label>
          <S.Title>{activeOpportunity?.title}</S.Title>
        </S.Header>

        <S.DataWrapper>
          <S.Data>
            <S.Label>{pageData.jobOverlayLocationLabel}</S.Label>
            <S.Value>{activeOpportunity?.offices[0]}</S.Value>
          </S.Data>
          <S.Data>
            <S.Label>{pageData.jobOverlayDepartmentLabel}</S.Label>
            <S.Value>{activeOpportunity?.departments[0]}</S.Value>
          </S.Data>
          <S.Data>
            <S.Label>{pageData.jobOverlayTypeLabel}</S.Label>
            <S.Value>{activeOpportunity?.metadata?.[0]?.value ?? '-'}</S.Value>
          </S.Data>
          <S.Data>
            <S.Label>{pageData.jobOverlayLevelLabel}</S.Label>
            <S.Value>-</S.Value>
          </S.Data>
        </S.DataWrapper>

        <S.Socials>
          <S.Label>{pageData.jobOverlayShareLabel}:</S.Label>
          <S.ShareScroll>
            {['Facebook', 'X', 'LinkedIn', 'Copy link'].map(social => (
              <S.Badge
                key={`opportunity-${social}`}
                onClick={() => handleShare(social)}
              >
                {social}
              </S.Badge>
            ))}
          </S.ShareScroll>
        </S.Socials>
      </S.HeaderWrapper>

      <S.Content
        dangerouslySetInnerHTML={{
          __html: safeHtml,
        }}
      />

      <Button
        label={pageData.jobOverlayApplyCtaLabel}
        target="_blank"
        href={activeOpportunity?.absolute_url}
      />
    </S.JoinUsOpportunityViewWrapper>
  );
};

JoinUsOpportunityView.defaultProps = defaultProps;

export default JoinUsOpportunityView;
